// Redesigned EventSettings.js
// Changes: Similar to NewEvent, but for editing existing event.
// - Bootstrap accordions, responsive forms.
// - Icons for labels/actions.
// - User-friendly: Pre-populate from API, confirmation on save.
// - Responsive: Full-width on mobile.
// - Integrated isprivate toggle.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/ApiServices';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle, FaSave, FaPlus, FaLock, FaMoneyBillWave, FaImage, FaSortNumericDown } from 'react-icons/fa';
import LoadingOverlay from '../shared/LoadingOverlay';
import useLoading from '../shared/useLoading';

const EventSettingsForm = () => {
  const { id } = useParams();
  const { isLoading, withLoading } = useLoading();

  // Event Details
  const [nameSection, setNameSection] = useState(true);
  const [eventName, setEventName] = useState('');
  const [duration, setDuration] = useState({ normalvalue: '15 mins', customvalue: ['15', 'mins'] });
  const [locationtype, setLocationtype] = useState('GOOGLE_MEET_AND_CALENDAR');
  const [locationDetails, setLocationDetails] = useState('');

  const [isprivate, setIsprivate] = useState(false);
  const [approvalNeeded, setApprovalNeeded] = useState(false);
  const [description, setDescription] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [eventImageFile, setEventImageFile] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const [existingEventImageUrl, setExistingEventImageUrl] = useState(null);

  // Event Settings
  const [isSettingOpen, setIsSettingOpen] = useState(true);
  const [dateRangeType, setDateRangeType] = useState('days');
  const [dateRangeDays, setDateRangeDays] = useState(60);
  const [bufferBefore, setBufferBefore] = useState(15);
  const [bufferAfter, setBufferAfter] = useState(15);
  const [startTimeIncrement, setStartTimeIncrement] = useState(15);
  const [inviteeBuffer, setInviteeBuffer] = useState(4);
  const [maxBookingsPerTimeslot, setMaxBookingsPerTimeslot] = useState(3);
  const [isPaid, setIsPaid] = useState(false);
  const [eventAmt, setEventAmt] = useState(0);
  const [isDefaultConfirm, setIsDefaultConfirm] = useState(true);
  const [customConfirm, setCustomConfirm] = useState('');
  const [isEventSettingCreated, setIsEventSettingCreated] = useState(false);

  // Notifications
  const [isEmailRemindersOpen, setIsEmailRemindersOpen] = useState(true);
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(true);
  const [reminderSubject, setReminderSubject] = useState('Reminder: Event Name is scheduled to start at Event Time');
  const [reminderBody, setReminderBody] = useState(`Hi My Name,
This is a friendly reminder that your Event Name will start at Event Time. Here are the event details:
Event Description: Event Description
Your information: Question And Answers
Reschedule Url | Cancel Url`);
  const [reminderTiming, setReminderTiming] = useState(24);

  const durationOptions = [
    { value: '15 mins', label: '15 mins' },
    { value: '30 mins', label: '30 mins' },
    { value: '1 hour', label: '1 hour' },
    { value: '1 hour 30 mins', label: '1 hour 30 mins' },
    { value: '2 hours', label: '2 hours' },
    { value: 'custom', label: 'Custom' }
  ];

  const getEventData = withLoading(async () => {
    try {
      const response = await apiService.get(`/api/events/events/${id}`);
      const event = response.event;
      setEventName(event.title || '');
      setDuration(event.duration ? JSON.parse(event.duration) : { normalvalue: '15 mins', customvalue: ['15', 'mins'] });
      setDescription(event.description || '');
      setLocationtype(event.locationtype || 'GOOGLE_MEET_AND_CALENDAR');
      setLocationDetails(event.location_details || '');
      setIsprivate(event.isprivate || false);
      setApprovalNeeded(event.approval_needed || false);
      setOrderBy(event.order_by != null ? event.order_by : '');
      if (event.event_image_url) {
        setExistingEventImageUrl(event.event_image_url);
      }

      const settings = event.eventSettings;
      if (settings) {
        setDateRangeType(settings.date_range_type || 'days');
        setDateRangeDays(settings.date_range_days || 60);
        setBufferBefore(settings.buffer_before || 15);
        setBufferAfter(settings.buffer_after || 15);
        setStartTimeIncrement(settings.start_time_increment || 15);
        setInviteeBuffer(settings.invitee_buffer || 4);
        setMaxBookingsPerTimeslot(settings.max_bookings_per_timeslot || 3);
        setIsPaid(settings.is_paid || false);
        setEventAmt(settings.event_amt || 0);
        setIsDefaultConfirm(settings.is_default_confirm || true);
        setCustomConfirm(settings.custom_confirm || '');
        setIsEventSettingCreated(true);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      alert('Failed to load event data.');
    }
  });

  useEffect(() => {
    getEventData();
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventImageFile(file);
      setEventImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateEvent = withLoading(async (e) => {
    e.preventDefault();
    if (!eventName.trim()) {
      alert('Event name is required.');
      return;
    }
    if (locationtype == 'IN_PERSON' && locationDetails.trim() == '') {
      alert('Location details are required.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', eventName);
      formData.append('duration', JSON.stringify(duration));
      formData.append('description', description);
      formData.append('locationtype', locationtype);
      formData.append('isprivate', isprivate);
      formData.append('location_details', locationDetails);
      formData.append('approval_needed', approvalNeeded);
      formData.append('order_by', orderBy !== '' ? parseInt(orderBy, 10) : '');
      if (eventImageFile) {
        formData.append('event_image', eventImageFile);
      }

      const response = await apiService.put(`/api/events/events/${id}`, formData);
      console.log('Event updated:', response.data);
      alert('Event updated successfully!');
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event.');
    }
  });

  const handleEventSettingSubmit = withLoading(async (e) => {
    e.preventDefault();
    try {
      const eventSettings = {
        date_range_type: dateRangeType,
        date_range_days: dateRangeDays,
        buffer_before: bufferBefore,
        buffer_after: bufferAfter,
        start_time_increment: startTimeIncrement,
        invitee_buffer: inviteeBuffer,
        max_bookings_per_timeslot: maxBookingsPerTimeslot,
        is_paid: isPaid,
        event_amt: isPaid ? Number(eventAmt) : 0,
        is_default_confirm: isDefaultConfirm,
        custom_confirm: customConfirm
      };
      const method = isEventSettingCreated ? 'put' : 'post';
      const endpoint = `/api/events/event-settings/${id}`;
      const response = await apiService[method](endpoint, eventSettings);
      console.log('Settings saved:', response.data);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings.');
    }
  });

  const handleNotificationsSave = () => {
    if (!reminderSubject.trim() || !reminderBody.trim()) {
      alert('Subject and body are required.');
      return;
    }
    alert('Notifications settings saved!');
  };

  return (
    <div className="container py-4">
      <LoadingOverlay isLoading={isLoading} />
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <h1 className="text-center mb-4">Edit Event Settings</h1>

          {/* Event Details */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 d-flex justify-content-between align-items-center  text-bg-primary" onClick={() => setNameSection(!nameSection)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaCalendarAlt className="me-2" /> Event Details</h5>
              <span>{nameSection ? '−' : '+'}</span>
            </div>
            {nameSection && (
              <form onSubmit={handleUpdateEvent} className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Event Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="Enter event name"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaClock className="me-1" /> Meeting Duration <FaInfoCircle className="text-primary ms-1" title="Info" />
                    </label>
                    <select
                      className="form-select"
                      value={duration.normalvalue}
                      onChange={(e) => setDuration({ ...duration, normalvalue: e.target.value })}
                    >
                      {durationOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {duration.normalvalue === 'custom' && (
                      <div className="mt-2 d-flex">
                        <input
                          type="number"
                          className="form-control w-auto me-2"
                          value={duration.customvalue[0]}
                          onChange={(e) => setDuration({ ...duration, customvalue: [e.target.value, duration.customvalue[1]] })}
                          placeholder="e.g., 45"
                          style={{ width: '80px' }}
                        />
                        <select
                          className="form-select w-auto"
                          value={duration.customvalue[1]}
                          onChange={(e) => setDuration({ ...duration, customvalue: [duration.customvalue[0], e.target.value] })}
                          style={{ width: '100px' }}
                        >
                          <option value="mins">mins</option>
                          <option value="hours">hours</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaMapMarkerAlt className="me-1" /> Location <FaInfoCircle className="text-primary ms-1" title="Info" />
                    </label>
                    <select
                      className="form-select"
                      value={locationtype}
                      onChange={(e) => setLocationtype(e.target.value)}
                    >
                      <option value="GOOGLE_MEET_AND_CALENDAR">Google Meet</option>
                      <option value="ZOOM_MEETING">Zoom</option>
                      <option value="MICROSOFT_TEAMS">Microsoft Teams</option>
                      <option value="IN_PERSON">In Person</option>
                      <option value="PHONE">Phone</option>
                    </select>
                  </div>
                  {
                    locationtype === 'IN_PERSON' && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          <FaMapMarkerAlt className="me-1" /> Location Details <FaInfoCircle className="text-primary ms-1" title="Info" />
                        </label>
                        <input
                          className="form-control"
                          type='text'
                          value={locationDetails}
                          onChange={(e) => setLocationDetails(e.target.value)}
                        >
                        </input>
                      </div>
                    )
                  }

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Private Event</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isprivate}
                        onChange={(e) => setIsprivate(e.target.checked)}
                      />
                      <label className="form-check-label">
                        <FaLock className="me-1" /> Make this a secret event
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Require Approval</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={approvalNeeded}
                        onChange={(e) => setApprovalNeeded(e.target.checked)}
                      />
                      <label className="form-check-label">Approval needed before booking</label>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaSortNumericDown className="me-1" /> Order By <FaInfoCircle className="text-primary ms-1" title="Display order for this event" />
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderBy}
                      onChange={(e) => setOrderBy(e.target.value)}
                      placeholder="e.g., 1"
                      min={0}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaImage className="me-1" /> Event Image <FaInfoCircle className="text-primary ms-1" title="Upload an image for this event" />
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {(eventImagePreview || existingEventImageUrl) && (
                      <div className="mt-2">
                        <img
                          src={eventImagePreview || existingEventImageUrl}
                          alt="Event preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                        />
                        {!eventImagePreview && existingEventImageUrl && (
                          <div className="form-text">Current event image. Upload a new file to replace it.</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="col-12 mb-3">
                    <label className="form-label">Description <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter event description"
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary">
                  <FaSave className="me-1" /> Update Event
                </button>
              </form>
            )}
          </div>

          {/* Event Settings */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 text-bg-primary d-flex justify-content-between align-items-center" onClick={() => setIsSettingOpen(!isSettingOpen)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaClock className="me-2" /> Event Settings</h5>
              <span>{isSettingOpen ? '−' : '+'}</span>
            </div>
            {isSettingOpen && (
              <form onSubmit={handleEventSettingSubmit} className="card-body">
                {/* Similar structure as NewEvent for settings fields */}
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Date Range <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="dateRange"
                        checked={dateRangeType === 'days'}
                        onChange={() => setDateRangeType('days')}
                      />
                      <label className="form-check-label">
                        {dateRangeDays} Calendar Days <input
                          type="number"
                          className="form-control d-inline-block w-auto ms-2"
                          style={{ width: '80px' }}
                          value={dateRangeDays}
                          onChange={(e) => setDateRangeDays(Number(e.target.value))}
                        /> into the future
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="dateRange"
                        checked={dateRangeType === 'indefinite'}
                        onChange={() => setDateRangeType('indefinite')}
                      />
                      <label className="form-check-label">Indefinitely into the future</label>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Buffer Before Event <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <select className="form-select" value={bufferBefore} onChange={(e) => setBufferBefore(Number(e.target.value))}>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Buffer After Event <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <select className="form-select" value={bufferAfter} onChange={(e) => setBufferAfter(Number(e.target.value))}>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Start Time Increments <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <select className="form-select" value={startTimeIncrement} onChange={(e) => setStartTimeIncrement(Number(e.target.value))}>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Invitees can't schedule within <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control w-auto"
                        value={inviteeBuffer}
                        onChange={(e) => setInviteeBuffer(Number(e.target.value))}
                        style={{ width: '80px' }}
                      />
                      <span className="input-group-text">mins of an event start time</span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Max Bookings Per Timeslot <FaInfoCircle className="text-primary ms-1" title="Maximum number of simultaneous bookings allowed per timeslot" /></label>
                    <input
                      type="number"
                      className="form-control"
                      value={maxBookingsPerTimeslot}
                      onChange={(e) => setMaxBookingsPerTimeslot(Number(e.target.value))}
                      min={1}
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label"><FaMoneyBillWave className="me-1" /> Paid Event</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isPaid}
                        onChange={(e) => {
                          setIsPaid(e.target.checked);
                          if (!e.target.checked) setEventAmt(0);
                        }}
                      />
                      <label className="form-check-label">This is a paid event</label>
                    </div>
                  </div>
                  {isPaid && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Event Amount <FaInfoCircle className="text-primary ms-1" title="Amount to charge for this event" /></label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          className="form-control"
                          value={eventAmt}
                          onChange={(e) => setEventAmt(Number(e.target.value))}
                          min={0}
                          placeholder="e.g., 500"
                          required
                        />
                      </div>
                    </div>
                  )}
                  <div className="col-md-6 mb-3">
                    <div className="col-md-6 mb-3">
                      <label className="form-label"><FaMoneyBillWave className="me-1" /> Default confirmation message</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isDefaultConfirm}
                          onChange={(e) => {
                            setIsDefaultConfirm(e.target.checked);
                            if (!e.target.checked) setCustomConfirm('');
                          }}
                        />

                      </div>
                    </div>
                  </div>
                  {/* Custom confirmation message field - show only when custom is selected */}
                  {!isDefaultConfirm && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Custom confirmation message <FaInfoCircle className="text-primary ms-1" title="Message to show after booking" /></label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={customConfirm}
                        onChange={(e) => setCustomConfirm(e.target.value)}
                        placeholder="Enter custom confirmation message"
                        required
                      />
                    </div>
                  )}
                </div>
                <button type="submit" className="btn btn-primary">
                  <FaSave className="me-1" /> Save Settings
                </button>
              </form>
            )}
          </div>

          {/* Notifications Policy */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 text-bg-primary d-flex justify-content-between align-items-center" onClick={() => setIsEmailRemindersOpen(!isEmailRemindersOpen)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaInfoCircle className="me-2" /> Notifications Policy</h5>
              <span>{isEmailRemindersOpen ? '−' : '+'}</span>
            </div>
            {isEmailRemindersOpen && (
              <div className="card-body">
                <p className="text-muted mb-3">Email Reminders, Calendar Invitations, Create no Event in CRM</p>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <p className="text-muted">An invitee will receive a reminder email before a scheduled event at specified times.</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <button className="btn btn-link p-0">Close</button>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={emailRemindersEnabled}
                          onChange={(e) => setEmailRemindersEnabled(e.target.checked)}
                        />
                        <label className="form-check-label">Enabled</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Subject <span className="text-primary">+Variables</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={reminderSubject}
                      onChange={(e) => setReminderSubject(e.target.value)}
                      placeholder="Enter subject"
                    />
                    <button className="btn btn-link p-0 mt-1">Restore Default</button>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Body <span className="text-primary">+Variables</span></label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={reminderBody}
                      onChange={(e) => setReminderBody(e.target.value)}
                      placeholder="Enter body"
                    />
                    <button className="btn btn-link p-0 mt-1">Restore Default</button>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Timing</label>
                    <select
                      className="form-select"
                      value={reminderTiming}
                      onChange={(e) => setReminderTiming(Number(e.target.value))}
                    >
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={i + 1}>{i + 1} Hour(s) before</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button className="btn btn-outline-primary mb-3">
                  <FaPlus className="me-1" /> Add Another Reminder
                </button>
                <button onClick={handleNotificationsSave} className="btn btn-success">
                  <FaSave className="me-1" /> Save Settings
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSettingsForm;
